import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { EaveType } from "building-types/settings/settingsTypesEnum";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { aabb2Height } from "geom/aabb2Types";
import { clippingNodeNew, clipRails3 } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { polyLine2AABB2, polyLine2Copy, polyLine2Simplify } from "geom/polyLine2";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3Copy, polyLine3Move, polyLine3Simplify } from "geom/polyLine3";
import { pushMissingProfileReferenceIssue } from "../elements/profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../elements/profileTools";
import { getSoffitDepth, getSquaredBoxOpenSoffitBottomOffset, hasValidFriezeBoards } from "../elements/roofTrimUtility";

interface FriezeBoardAlongRailOptions {
  rail: PolyLine3;
  slope: number;
  overhang: number;
  profileSource: ActiveProfileSource;
  roofSettings: RoofSettings;
  faceArrays: FaceArrayViewEntity[];
  friezePlaneNormal: [number, number, number];
  eaveType?: EaveType;
  sweepProfile?: FriezeBoard[];
  hasSquaredCut?: boolean;
  issues?: BuildingGenerationIssue[];
  handle?: DocEntityHandle;
}

export function friezeBoardAlongRail({
  rail,
  slope,
  overhang,
  profileSource,
  roofSettings,
  faceArrays,
  friezePlaneNormal,
  eaveType,
  sweepProfile,
  hasSquaredCut,
  issues,
  handle,
}: FriezeBoardAlongRailOptions) {
  if (eaveType === EaveType.ANGLED_OPEN_OPEN || !hasValidFriezeBoards(sweepProfile)) {
    return;
  }

  assertIsNotUndefined(sweepProfile);

  for (const { friezeBoard, verticalOffset = 0, lateralOffset = 0, friezeBoardMaterial } of sweepProfile) {
    const descriptor = getActiveProfileDescriptor(profileSource, friezeBoard, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
    if (!descriptor) {
      pushMissingProfileReferenceIssue(issues, profileSource, friezeBoard, handle, "friezeBoard");
      continue;
    }
    const profile = getProfileExtrusionDescriptorProfile(descriptor);
    const friezeBoardProfile = polyLine2Simplify(polyLine2Copy(profile));
    const friezeBoardProfileAABB = polyLine2AABB2(friezeBoardProfile);

    let offsetZ = -friezeBoardProfileAABB.maxY - verticalOffset;

    if (eaveType === EaveType.ANGLED_ENCLOSED_OPEN) {
      if (slope === Number.POSITIVE_INFINITY) {
        offsetZ += +roofSettings.eaveFasciaSize + aabb2Height(friezeBoardProfileAABB);
      } else {
        const soffitDepth = getSoffitDepth(roofSettings);
        const soffitOffset = Math.sqrt(soffitDepth * soffitDepth * (1 + slope * slope));
        offsetZ += slope * overhang + roofSettings.eaveFasciaSize - soffitOffset;
      }
    } else if (eaveType === EaveType.BOXED_OPEN && hasSquaredCut) {
      const soffitDepth = getSoffitDepth(roofSettings);
      offsetZ += getSquaredBoxOpenSoffitBottomOffset(soffitDepth, slope, overhang);
    }

    let friezeRail = polyLine3Copy(rail);
    const [nX, nY, nZ] = friezePlaneNormal;
    friezeRail = polyLine3Move(friezeRail, -nX * lateralOffset, -nY * lateralOffset, -nZ * lateralOffset + offsetZ);
    friezeRail = polyLine3Simplify(friezeRail);

    const brep = extrudeFaceAlongPolyLine3([friezeBoardProfile], friezeRail, 0, 0, 1);

    faceArrays.push(
      faceArrayViewEntityNew(brep.faces, descriptor, friezeBoardMaterial ?? roofSettings.eaveFriezeBoardMaterialLegacy),
    );
  }
}

interface ClipFriezeRailsOutsideWallsOptions {
  wallOverlapForFriezeBoardBSPs: NodeBSP[];
  friezeRails: PolyLine3[];
}

export function clipFriezeRailsOutsideWalls({
  wallOverlapForFriezeBoardBSPs,
  friezeRails,
}: ClipFriezeRailsOutsideWallsOptions): PolyLine3[] {
  const finalFriezeRails: PolyLine3[] = [];

  for (const wallOverlapBSP of wallOverlapForFriezeBoardBSPs) {
    clipRails3({
      rails: friezeRails,
      options: {
        clippingNodes: [clippingNodeNew(wallOverlapBSP, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
        coplanarsToFront: false,
      },
      clippedRails: finalFriezeRails,
    });
  }

  return finalFriezeRails;
}
